<template>
  <div class="ap-card">
    <header class="ap-header">
      <div>
        <h5 class="ap-title">Alertas e anomalias</h5>
        <p class="ap-subtitle">
          {{ totalAlerts }} {{ totalAlerts === 1 ? 'alerta' : 'alertas' }}
          {{ totalAlerts === 0 ? 'no momento' : 'detectado' + (totalAlerts > 1 ? 's' : '') }}
        </p>
      </div>
      <span v-if="totalAlerts > 0" class="ap-pulse-badge">
        <span class="ap-pulse-dot"></span>
        ATIVO
      </span>
    </header>

    <!-- Pico atípico (mais visível quando ativo) -->
    <div v-if="peak.is_atypical" class="ap-peak-banner">
      <div class="ap-peak-icon"><i class="bx bx-trending-up"></i></div>
      <div class="ap-peak-body">
        <div class="ap-peak-title">Pico atípico de check-ins</div>
        <div class="ap-peak-detail">
          <strong>{{ peak.current_hour_count }}</strong> nesta hora
          —
          <strong>{{ peak.multiple_of_avg }}×</strong> a média das 4 horas anteriores
          (<span class="ap-muted">{{ peak.past_4h_avg }}/h</span>)
        </div>
      </div>
    </div>

    <!-- Empty quando tudo zerado -->
    <div v-if="isAllClean" class="ap-clean">
      <div class="ap-clean-icon">
        <i class="bx bx-check-shield"></i>
      </div>
      <p class="ap-clean-title">Tudo tranquilo</p>
      <small>Nenhuma anomalia detectada nas últimas 24h</small>
    </div>

    <template v-else>
      <!-- Tags silenciosas -->
      <section v-if="staleTags.length > 0" class="ap-section">
        <div class="ap-section-head">
          <i class="bx bx-volume-mute ap-section-icon"></i>
          <span class="ap-section-title">Tags silenciosas</span>
          <span class="ap-section-count">{{ staleTags.length }}</span>
        </div>
        <div class="ap-stale-grid">
          <div
            v-for="tag in staleTags.slice(0, 4)"
            :key="tag.tag_id"
            class="ap-stale-tag"
            :class="staleSeverity(tag.minutes_silent)"
          >
            <div class="ap-stale-name" :title="tag.tag_name">{{ tag.tag_name }}</div>
            <div class="ap-stale-time">
              <i class="bx bx-time-five"></i>
              {{ formatStale(tag.minutes_silent) }}
            </div>
          </div>
        </div>
      </section>

      <!-- Negadas recentes -->
      <section v-if="deniedRecent.length > 0" class="ap-section">
        <div class="ap-section-head">
          <i class="bx bx-shield-x ap-section-icon"></i>
          <span class="ap-section-title">Leituras negadas</span>
          <span class="ap-section-count">{{ deniedRecent.length }}</span>
        </div>
        <div class="ap-timeline">
          <div
            v-for="item in deniedRecent.slice(0, 6)"
            :key="item.id"
            class="ap-timeline-row"
          >
            <span class="ap-timeline-pin" :style="{ background: pinColor(item.created_at) }"></span>
            <span class="ap-timeline-time">{{ formatTime(item.created_at) }}</span>
            <span class="ap-timeline-name" :title="item.participant_name || 'Desconhecido'">
              {{ item.participant_name || 'QR Inválido' }}
            </span>
            <span class="ap-timeline-detail" :title="item.type">
              {{ stripTagSuffix(item.type) }}
            </span>
          </div>
        </div>
      </section>

      <!-- Reincidentes -->
      <section v-if="repeatOffenders.length > 0" class="ap-section">
        <div class="ap-section-head">
          <i class="bx bx-error ap-section-icon"></i>
          <span class="ap-section-title">Tentativas reincidentes</span>
          <span class="ap-section-count">{{ repeatOffenders.length }}</span>
        </div>
        <div class="ap-offenders">
          <div
            v-for="off in repeatOffenders.slice(0, 5)"
            :key="off.participant_id"
            class="ap-offender-row"
          >
            <span class="ap-offender-name" :title="off.participant_name">
              {{ off.participant_name || ('Participante #' + off.participant_id) }}
            </span>
            <span class="ap-offender-badge">
              <i class="bx bx-x"></i>
              {{ off.attempts }} tentativas
            </span>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
export default {
  name: 'anomalies-panel',
  props: {
    anomalies: { type: Object, default: null }
  },
  computed: {
    deniedRecent () { return (this.anomalies && this.anomalies.denied_recent) || [] },
    staleTags () { return (this.anomalies && this.anomalies.stale_tags) || [] },
    repeatOffenders () { return (this.anomalies && this.anomalies.repeat_offenders) || [] },
    peak () {
      return (this.anomalies && this.anomalies.current_peak) || {
        is_atypical: false, current_hour_count: 0, past_4h_avg: 0, multiple_of_avg: null
      }
    },
    totalAlerts () {
      return this.deniedRecent.length + this.staleTags.length + this.repeatOffenders.length + (this.peak.is_atypical ? 1 : 0)
    },
    isAllClean () {
      return this.totalAlerts === 0
    }
  },
  methods: {
    staleSeverity (minutes) {
      if (minutes >= 60) return 'ap-stale-critical'
      if (minutes >= 30) return 'ap-stale-warn'
      return 'ap-stale-mild'
    },
    formatStale (minutes) {
      if (minutes < 60) return `há ${minutes}min`
      const h = Math.floor(minutes / 60)
      return `há ${h}h`
    },
    formatTime (datetime) {
      if (!datetime) return ''
      const d = new Date(datetime.replace ? datetime.replace(' ', 'T') : datetime)
      return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    },
    /**
     * Cor do pin lateral baseada em quão recente foi a tentativa.
     * Mais recente = mais vermelho (urgente). Mais antigo = cinza.
     */
    pinColor (datetime) {
      if (!datetime) return '#d0d5dd'
      const d = new Date(datetime.replace ? datetime.replace(' ', 'T') : datetime)
      const minutes = (Date.now() - d.getTime()) / 60000
      if (minutes < 5) return '#f46a6a'
      if (minutes < 60) return '#f1b44c'
      return '#8b90a5'
    },
    /**
     * Se o tipo da activity tem ", TAG X" no final (sufixo introduzido pelo
     * backend pra histórico), pega só o sufixo pra mostrar de forma compacta.
     */
    stripTagSuffix (type) {
      if (!type) return ''
      const idx = type.indexOf(', TAG ')
      if (idx === -1) return type
      return type.substring(idx + 2) // "TAG X"
    }
  }
}
</script>

<style lang="scss" scoped>
.ap-card {
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  padding: 22px 24px;
  box-shadow: 0 1px 3px rgba(26, 29, 38, 0.04);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.ap-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.ap-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1d26;
  margin: 0 0 4px 0;
}

.ap-subtitle {
  font-size: 12px;
  color: #8b90a5;
  margin: 0;
}

.ap-pulse-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  background: rgba(244, 106, 106, 0.1);
  color: #d04848;
  border-radius: 99px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
}

.ap-pulse-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #f46a6a;
  animation: ap-pulse 1.6s ease-out infinite;
}

@keyframes ap-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(244, 106, 106, 0.6); }
  70%  { box-shadow: 0 0 0 6px rgba(244, 106, 106, 0); }
  100% { box-shadow: 0 0 0 0 rgba(244, 106, 106, 0); }
}

/* Pico atípico — banner vibrante */
.ap-peak-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: linear-gradient(120deg, rgba(244, 106, 106, 0.1), rgba(241, 180, 76, 0.1));
  border: 1px solid rgba(244, 106, 106, 0.25);
  border-radius: 10px;
  margin-bottom: 16px;
}

.ap-peak-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #f46a6a, #f1b44c);
  color: #fff;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  font-size: 18px;
  box-shadow: 0 4px 12px rgba(244, 106, 106, 0.25);
}

.ap-peak-title {
  font-size: 13px;
  font-weight: 700;
  color: #1a1d26;
  margin-bottom: 2px;
}

.ap-peak-detail {
  font-size: 11px;
  color: #4a5068;
  font-weight: 500;

  strong { color: #1a1d26; font-weight: 700; }
}

.ap-muted { color: #8b90a5; }

/* Estado limpo */
.ap-clean {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px 24px;
}

.ap-clean-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(52, 195, 143, 0.12), rgba(85, 110, 230, 0.12));
  color: #34c38f;
  display: grid;
  place-items: center;
  font-size: 28px;
  margin-bottom: 12px;
}

.ap-clean-title {
  font-size: 13px;
  font-weight: 700;
  color: #1a1d26;
  margin: 0 0 4px 0;
}

.ap-clean small {
  font-size: 12px;
  color: #8b90a5;
}

/* Sections genéricas */
.ap-section {
  margin-bottom: 18px;
  &:last-child { margin-bottom: 0; }
}

.ap-section-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eef0f6;
}

.ap-section-icon {
  font-size: 16px;
  color: #4a5068;
}

.ap-section-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #1a1d26;
  flex: 1;
}

.ap-section-count {
  background: #1a1d26;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 99px;
  font-variant-numeric: tabular-nums;
}

/* Tags silenciosas */
.ap-stale-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
}

.ap-stale-tag {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid;
  display: flex;
  flex-direction: column;
  gap: 2px;

  &.ap-stale-mild {
    background: rgba(241, 180, 76, 0.06);
    border-color: rgba(241, 180, 76, 0.3);
    .ap-stale-time { color: #d6952f; }
  }
  &.ap-stale-warn {
    background: rgba(241, 180, 76, 0.12);
    border-color: rgba(241, 180, 76, 0.5);
    .ap-stale-time { color: #b8801f; }
  }
  &.ap-stale-critical {
    background: rgba(244, 106, 106, 0.08);
    border-color: rgba(244, 106, 106, 0.4);
    .ap-stale-time { color: #d04848; }
  }
}

.ap-stale-name {
  font-size: 12px;
  font-weight: 700;
  color: #1a1d26;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ap-stale-time {
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 3px;

  i { font-size: 13px; }
}

/* Timeline de negadas */
.ap-timeline {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ap-timeline-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 8px;
  font-size: 12px;
  transition: background 0.15s ease;

  &:hover { background: #fafbfc; }
}

.ap-timeline-pin {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.8);
}

.ap-timeline-time {
  font-size: 10px;
  font-weight: 700;
  color: #4a5068;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  width: 38px;
}

.ap-timeline-name {
  flex: 1;
  font-size: 12px;
  font-weight: 600;
  color: #1a1d26;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.ap-timeline-detail {
  font-size: 10px;
  color: #8b90a5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 110px;
  font-weight: 500;
}

/* Reincidentes */
.ap-offenders {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ap-offender-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 8px;
  transition: background 0.15s ease;

  &:hover { background: rgba(244, 106, 106, 0.05); }
}

.ap-offender-name {
  font-size: 12px;
  font-weight: 600;
  color: #1a1d26;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.ap-offender-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  background: rgba(244, 106, 106, 0.1);
  color: #d04848;
  border-radius: 99px;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;

  i { font-size: 12px; }
}
</style>
